import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

export default function OathApplication() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [idCard, setIdCard] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [birthday, setBirthday] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [fileName, setFileName] = useState('');
  const [signatureData, setSignatureData] = useState('');
  const [showSignModal, setShowSignModal] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);

  const startDraw = useCallback((x: number, y: number) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    isDrawing.current = true;
    ctx.beginPath();
    ctx.moveTo(x, y);
  }, []);

  const draw = useCallback((x: number, y: number) => {
    if (!isDrawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.lineTo(x, y);
    ctx.stroke();
  }, []);

  const stopDraw = useCallback(() => { isDrawing.current = false; }, []);

  const getPos = (e: React.TouchEvent | React.MouseEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top };
  };

  useEffect(() => {
    if (showSignModal) setTimeout(initCanvas, 50);
  }, [showSignModal]);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, []);

  const confirmSign = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSignatureData(canvas.toDataURL('image/png'));
    setShowSignModal(false);
  }, []);

  const today = new Date();
  const signDate = `${today.getFullYear()}年 ${String(today.getMonth() + 1).padStart(2, '0')}月 ${String(today.getDate()).padStart(2, '0')}日`;

  useEffect(() => {
    Promise.all([
      api.get('/asset-restore/status'),
      api.get('/user-pledge/status'),
    ]).then(([assetRes, pledgeRes]) => {
      if (!assetRes.data.has_applied) {
        navigate('/asset-restore', { replace: true });
        return;
      }
      if (pledgeRes.data.has_applied) {
        setHasApplied(true);
        const app = pledgeRes.data.application;
        setName(app.name || '');
        setIdCard(app.id_card || '');
        setPhone(app.phone || '');
        setAddress(app.address || '');
        setBirthday(app.birthday || '');
        if (app.signature) setSignatureData(app.signature);
        const s = app.status === 'approved' ? '已通过' : app.status === 'rejected' ? '已拒绝' : '审核中';
        setStatusText(s);
      }
    }).catch(() => { });
  }, []);

  async function handleSubmit() {
    if (hasApplied || submitting) return;
    if (!name.trim()) {
      toast.error('请填写申请人姓名');
      return;
    }
    if (!agreed) {
      toast.error('请阅读并同意《宣誓承诺书》内容');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/user-pledge', {
        name: name.trim(),
        id_card: idCard.trim() || null,
        phone: phone.trim() || null,
        address: address.trim() || null,
        birthday: birthday || null,
        sign_date: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`,
        signature: signatureData || null,
      });
      toast.success('提交成功');
      setHasApplied(true);
      setStatusText('已通过');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-md mx-auto min-h-screen pb-3" style={{ backgroundColor: '#d81b1c' }}>
      <AuthGuard />

      {/* Hero Banner */}
      <div className="relative">
        <img src="/images/xuanshi_shang.png" alt="" className="w-full" />
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 text-black/80"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      </div>

      {/* Application Form Section */}
      <div
        className="mt-[-50px] ml-[13px] mr-[15px] bg-white pl-[11px] pr-[12px]"
      >
        <div
          className="pt-[30px] pb-[10px] overflow-hidden relative"
          style={{ backgroundImage: 'url(/images/xuanshi_kuang.png)', backgroundSize: '100% 100%', backgroundPosition: 'top center', backgroundRepeat: 'no-repeat' }}
        >
          <div className="divide-y divide-gray-50 relative z-10">
            {/* Row 1: Name */}
            <div className="flex items-center px-4 py-1.5 gap-3">
              <img src="/images/xuanshi_xingming.png" alt="" className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-700 w-20 flex-shrink-0">申请人姓名</span>
              <input
                type="text"
                placeholder="请输入真实姓名"
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={hasApplied}
                className="flex-1 text-xs text-right text-gray-800 placeholder-gray-300 outline-none bg-transparent disabled:opacity-60"
              />
            </div>

            {/* Row 2: ID */}
            <div className="flex items-center px-4 py-1.5 gap-3">
              <img src="/images/xuanshi_shenfenzhneg.png" alt="" className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-700 w-20 flex-shrink-0">身份证号码</span>
              <input
                type="text"
                placeholder="请输入身份证号码"
                value={idCard}
                onChange={e => setIdCard(e.target.value)}
                disabled={hasApplied}
                className="flex-1 text-xs text-right text-gray-800 placeholder-gray-300 outline-none bg-transparent disabled:opacity-60"
              />
            </div>

            {/* Row 3: Phone */}
            <div className="flex items-center px-4 py-1.5 gap-3">
              <img src="/images/xuanshi_dianhua.png" alt="" className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-700 w-20 flex-shrink-0">联系电话</span>
              <input
                type="tel"
                placeholder="请输入手机号码"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                disabled={hasApplied}
                className="flex-1 text-xs text-right text-gray-800 placeholder-gray-300 outline-none bg-transparent disabled:opacity-60"
              />
            </div>

            {/* Row 4: Address */}
            <div className="flex items-center px-4 py-1.5 gap-3">
              <img src="/images/xuanshi_juzhudi.png" alt="" className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-700 w-20 flex-shrink-0">现居住地</span>
              <input
                type="text"
                placeholder="请选择现居住地"
                value={address}
                onChange={e => setAddress(e.target.value)}
                disabled={hasApplied}
                className="flex-1 text-xs text-right text-gray-800 placeholder-gray-300 outline-none bg-transparent disabled:opacity-60"
              />
              <img src="/images/xuanshi_jiantou.png" alt="" className="w-2 h-3 flex-shrink-0 opacity-30" />
            </div>

            {/* Row 5: Birthday */}
            <div className="flex items-center px-4 py-1.5 gap-3">
              <img src="/images/xuanshi_nianyue.png" alt="" className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-700 w-20 flex-shrink-0">出生年月日</span>
              <input
                type="date"
                placeholder="请选择出生日期"
                value={birthday}
                onChange={e => setBirthday(e.target.value)}
                disabled={hasApplied}
                className="flex-1 text-xs text-right text-gray-800 placeholder-gray-300 outline-none bg-transparent disabled:opacity-60"
              />
              <img src="/images/xuanshi_jiantou.png" alt="" className="w-2 h-3 flex-shrink-0 opacity-30" />
            </div>

            {/* Row 6: Project (read-only) */}
            <div className="flex items-center px-4 py-1.5 gap-3">
              <img src="/images/xuanshi_xiangmy.png" alt="" className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-700 w-20 flex-shrink-0">申请项目</span>
              <span className="flex-1 text-xs text-right text-gray-800">云数家园资产恢复</span>
              <img src="/images/xuanshi_jiantou.png" alt="" className="w-2 h-3 flex-shrink-0 opacity-30" />
            </div>

            {/* Row 7: Amount (read-only, red) */}
            <div className="flex items-center px-4 py-1.5 gap-3">
              <img src="/images/xuanshi_jine.png" alt="" className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-700 w-20 flex-shrink-0">申请金额</span>
              <span className="flex-1 text-xs text-right text-[#d11010] font-bold">500万元退款资金</span>
            </div>

            {/* Row 8: Upload */}
            <div className="flex items-center px-4 py-1.5 gap-3">
              <img src="/images/xuanshi_cailiao.png" alt="" className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-700 w-20 flex-shrink-0">相关证明材料</span>
              <div className="flex justify-between items-center w-full">
                <span className={`text-xs text-left font-bold truncate mr-2 ${fileName ? 'text-gray-800' : 'text-gray-300'}`}>{fileName || '请上传身份证、损失凭证等材料'}</span>
                <label className="inline-flex items-center gap-1 text-[11px] text-white cursor-pointer relative flex-shrink-0">
                  <img src="/images/xuanshi_shanchaun.png" alt="" className="w-[60px] h-[20px]" />
                  <span className="absolute left-0 top-[2px] right-0 text-center">上传材料</span>
                  <input type="file" className="hidden" disabled={hasApplied} onChange={e => { if (e.target.files?.[0]) setFileName(e.target.files[0].name); }} />
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Oath Content Section */}
      <div
        className="mt-[0px] ml-[13px] mr-[15px] bg-white pl-[11px] pr-[12px] pt-[10px] pb-[10px] rounded-b-xl"
      >
        <div className="ml-[3px] mr-[2px]">
          <img src={"/images/xuanshi_xuanshi.png"} alt="xuanshi_xuanshi" className="w-full" />
        </div>


        {/* Agreement Checkbox */}
        <div className="mx-3 mb-3 mt-2 flex items-start gap-2 px-1">
          <button
            onClick={() => !hasApplied && setAgreed(!agreed)}
            disabled={hasApplied}
            className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition ${agreed ? 'bg-[#c4161c] border-[#f25e64]' : 'border-[#f25e64]/60 bg-transparent'} disabled:opacity-60`}
          >
            {agreed && (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
            )}
          </button>
          <span className="text-black/90 text-xs leading-relaxed mt-[2px]">
            我已阅读并同意《宣誓承诺书》内容
          </span>
        </div>

        {/* Signature Line */}
        <div className="mx-3 mb-4 flex justify-between items-center">
          <div className="flex items-center gap-1">
            <span className="text-black text-xs flex-shrink-0">申请人签名：</span>
            {signatureData ? (
              <img
                src={signatureData}
                alt="签名"
                className="h-8 cursor-pointer"
                onClick={() => !hasApplied && setShowSignModal(true)}
              />
            ) : (
              <button
                onClick={() => !hasApplied && setShowSignModal(true)}
                className="text-[#c4161c] text-xs underline"
                disabled={hasApplied}
              >
                点击手写签名
              </button>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-black text-xs">申请日期：</span>
            <span className="text-black/80 text-xs">{signDate}</span>
          </div>
        </div>

        {/* Submit Button */}
        <div className="px-4 mb-3">
          {hasApplied ? (
            <div className="text-center py-3">
              <div className="inline-block bg-[#fdf8f2] text-[#c4161c] font-bold text-sm px-6 py-3 rounded-full">
                已提交申请（{statusText}）
              </div>
            </div>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || !name.trim() || !agreed || !signatureData}
              className={`w-full transition ${(!name.trim() || !agreed || !signatureData) ? 'opacity-50' : ''}`}
            >
              <img src="/images/xuanshi_shenqing.png" alt="" className="w-full" />
            </button>
          )}
        </div>

        {/* Note */}
        <p className="text-center text-[#b62029] text-[10px] mb-4 px-4">
          提交后将由官方审核，请保持电话畅通
        </p>
        <div className="relative mb-3">
          <img src="/images/xuanshi_shenhe.png" alt="" className="w-full" />
        </div>
      </div>

      {/* Signature Modal */}
      {showSignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowSignModal(false)}>
          <div className="bg-white rounded-xl w-[90%] max-w-md p-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-center text-sm font-bold mb-3">手写签名</h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden mb-3">
              <canvas
                ref={canvasRef}
                className="w-full h-[200px] touch-none"
                style={{ background: '#fafafa' }}
                onMouseDown={e => { const p = getPos(e); startDraw(p.x, p.y); }}
                onMouseMove={e => { const p = getPos(e); draw(p.x, p.y); }}
                onMouseUp={stopDraw}
                onMouseLeave={stopDraw}
                onTouchStart={e => { e.preventDefault(); const p = getPos(e); startDraw(p.x, p.y); }}
                onTouchMove={e => { e.preventDefault(); const p = getPos(e); draw(p.x, p.y); }}
                onTouchEnd={stopDraw}
              />
            </div>
            <div className="flex gap-3">
              <button onClick={clearCanvas} className="flex-1 py-2 rounded-full border border-gray-300 text-sm text-gray-600">清除</button>
              <button onClick={confirmSign} className="flex-1 py-2 rounded-full bg-[#c4161c] text-white text-sm font-bold">确认</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
